import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Pencil, Trash2, Users, Loader2 } from "lucide-react";

interface Employee {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: string;
  status: string;
  hire_date: string | null;
  user_id: string | null;
  default_commission_rule_id: string | null;
  notes: string | null;
}

interface Permissions {
  can_view_all_jobs: boolean;
  can_edit_pricing: boolean;
  can_view_commissions: boolean;
  can_view_financials: boolean;
  can_manage_team: boolean;
  can_manage_settings: boolean;
  can_create_proposals: boolean;
  can_send_invoices: boolean;
  can_view_reports: boolean;
}

const ROLE_OPTIONS = [
  { value: "sales_rep", label: "Sales Rep" },
  { value: "project_manager", label: "Project Manager" },
  { value: "crew", label: "Crew" },
  { value: "office", label: "Office" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
];

const PERMISSION_LABELS: Record<keyof Permissions, string> = {
  can_view_all_jobs: "View all jobs",
  can_edit_pricing: "Edit pricing",
  can_view_commissions: "View commissions",
  can_view_financials: "View financials",
  can_manage_team: "Manage team",
  can_manage_settings: "Manage settings",
  can_create_proposals: "Create proposals",
  can_send_invoices: "Send invoices",
  can_view_reports: "View reports",
};

const defaultPermissions: Permissions = {
  can_view_all_jobs: false,
  can_edit_pricing: false,
  can_view_commissions: false,
  can_view_financials: false,
  can_manage_team: false,
  can_manage_settings: false,
  can_create_proposals: true,
  can_send_invoices: false,
  can_view_reports: false,
};

export function EmployeesPanel({ orgId }: { orgId: string }) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [rules, setRules] = useState<{ id: string; name: string }[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "sales_rep",
    status: "active",
    hire_date: "",
    default_commission_rule_id: "",
    notes: "",
  });
  const [perms, setPerms] = useState<Permissions>(defaultPermissions);
  const [pendingDelete, setPendingDelete] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (orgId) {
      load();
      loadRules();
    }
  }, [orgId]);

  const load = async () => {
    const { data, error } = await supabase
      .from("employees")
      .select("*")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error loading", description: error.message, variant: "destructive" });
      return;
    }
    setEmployees(data as any || []);
  };

  const loadRules = async () => {
    const { data } = await supabase
      .from("commission_rules")
      .select("id, name")
      .eq("org_id", orgId)
      .eq("is_active", true);
    setRules(data || []);
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      full_name: "",
      email: "",
      phone: "",
      role: "sales_rep",
      status: "active",
      hire_date: "",
      default_commission_rule_id: "",
      notes: "",
    });
    setPerms(defaultPermissions);
    setIsOpen(true);
  };

  const openEdit = async (e: Employee) => {
    setEditing(e);
    setForm({
      full_name: e.full_name,
      email: e.email || "",
      phone: e.phone || "",
      role: e.role,
      status: e.status,
      hire_date: e.hire_date || "",
      default_commission_rule_id: e.default_commission_rule_id || "",
      notes: e.notes || "",
    });
    const { data } = await supabase
      .from("employee_permissions")
      .select("*")
      .eq("employee_id", e.id)
      .maybeSingle();
    if (data) setPerms(data as any);
    else setPerms(defaultPermissions);
    setIsOpen(true);
  };

  const save = async () => {
    if (!form.full_name) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    const payload: any = {
      org_id: orgId,
      full_name: form.full_name,
      email: form.email || null,
      phone: form.phone || null,
      role: form.role,
      status: form.status,
      hire_date: form.hire_date || null,
      default_commission_rule_id: form.default_commission_rule_id || null,
      notes: form.notes || null,
    };

    let employeeId = editing?.id;
    if (editing) {
      const { error } = await supabase.from("employees").update(payload).eq("id", editing.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
    } else {
      const { data, error } = await supabase.from("employees").insert(payload).select().single();
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
      employeeId = data.id;
    }

    if (employeeId) {
      await supabase
        .from("employee_permissions")
        .upsert(
          { employee_id: employeeId, org_id: orgId, ...perms },
          { onConflict: "employee_id" }
        );
    }

    toast({ title: editing ? "Employee updated" : "Employee added" });
    setIsOpen(false);
    load();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase
        .from("employees")
        .delete()
        .eq("id", pendingDelete.id)
        .select("id");

      if (error) {
        toast({
          title: "Couldn't remove employee",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      if (!data || data.length === 0) {
        toast({
          title: "Nothing was removed",
          description:
            "You may not have permission to delete this employee, or they're already gone. Refresh and try again, or ask the org owner.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Employee removed" });
      setPendingDelete(null);
      load();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Employees
            </CardTitle>
            <CardDescription>Manage your team, roles, permissions and commission rules</CardDescription>
          </div>
          <Button onClick={openNew}>
            <UserPlus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {employees.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No employees yet. Add your first team member.
          </p>
        ) : (
          <div className="space-y-3">
            {employees.map((e) => (
              <div key={e.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {e.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{e.full_name}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{ROLE_OPTIONS.find((r) => r.value === e.role)?.label}</span>
                      {e.email && <span>• {e.email}</span>}
                      <Badge variant={e.status === "active" ? "default" : "secondary"}>{e.status}</Badge>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(e)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPendingDelete(e)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle>
              <DialogDescription>Set their info, role, commission rule and permissions</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Full Name</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Hire Date</Label>
                  <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Default Commission Rule</Label>
                <Select
                  value={form.default_commission_rule_id || "none"}
                  onValueChange={(v) => setForm({ ...form, default_commission_rule_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {rules.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-base font-semibold">Permissions</Label>
                <div className="grid grid-cols-2 gap-3 mt-2 p-4 border rounded-lg">
                  {(Object.keys(PERMISSION_LABELS) as (keyof Permissions)[]).map((key) => (
                    <div key={key} className="flex items-center justify-between">
                      <Label className="text-sm font-normal">{PERMISSION_LABELS[key]}</Label>
                      <Switch
                        checked={perms[key]}
                        onCheckedChange={(c) => setPerms({ ...perms, [key]: c })}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={save} className="w-full">
                {editing ? "Save Changes" : "Add Employee"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={!!pendingDelete}
          onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove employee?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove{" "}
                <span className="font-semibold">{pendingDelete?.full_name}</span>{" "}
                and any commission rules and permissions tied to them. Time-clock
                history will be kept. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  confirmDelete();
                }}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Removing…
                  </>
                ) : (
                  "Remove"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
